/**
 * Panneau Diagnostics partagé par les quatre pages détail.
 *
 * Extrait de RincageDetail (la version la plus riche : Nom, version firmware,
 * carte V3, coupures datées, santé du bus RS485) pour supprimer la
 * quadruplication : toute évolution des diagnostics se fait désormais ICI et
 * toutes les vues en héritent. Les cartes spécifiques V3 sont conditionnelles :
 * un boîtier V2 qui ne publie pas ces champs ne les voit pas.
 */
import React from 'react';
import { Box, Card, CardContent, Chip, Grid, LinearProgress, Typography } from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import SensorsIcon from '@mui/icons-material/Sensors';
import StorageIcon from '@mui/icons-material/Storage';
import WarningIcon from '@mui/icons-material/Warning';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const formatUptime = (seconds: number) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${days > 0 ? days + 'j ' : ''}${hours}h ${minutes}m ${secs}s`;
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * LOT 7.5 — rendu d'un champ absent.
 *
 * `formatBytes(x || 0)` rendait « 0 B » aussi bien pour un champ absent que
 * pour un volume réellement nul. Sur le disque, ce raccourci a produit
 * l'affichage « 0 B / 0 B, 0 % occupé, barre bleue » au moment précis où le
 * disque du boîtier était plein et où le collecteur ne pouvait plus écrire.
 */
const formatBytesOuIndisponible = (bytes: number | null | undefined) =>
  bytes === null || bytes === undefined ? 'indisponible' : formatBytes(bytes);

const getStatusInfo = (status: number | undefined) => {
  switch (status) {
    case 0:
      return { text: 'Connecté', color: 'success' as const };
    case 1:
      return { text: 'Attention', color: 'warning' as const };
    case 2:
      return { text: 'Erreur', color: 'error' as const };
    default:
      return { text: 'Inconnu', color: 'default' as const };
  }
};

const filtreParDefaut = (erreurs: any[] | undefined, capteurs: any): any[] => {
  const tout: any[] = [...(erreurs || [])];
  if (capteurs) {
    for (const [nom, data] of Object.entries(capteurs) as [string, any][]) {
      if ((data?.fail_count ?? 0) > 0 && data?.status === 2) {
        tout.push({ ts: new Date().toISOString(), code: 0, message: `Capteur ${nom} en échec`, count: data.fail_count });
      }
    }
  }
  return tout;
};

const DiagnosticsPanel: React.FC<{
  diagnostics: any;
  filtrerErreurs?: (erreurs: any[] | undefined, capteurs: any) => any[];
  libellePeriode?: string;
}> = ({ diagnostics, filtrerErreurs, libellePeriode }) => (
  <>
    {diagnostics && diagnostics.timestamp ? (
  <Grid container spacing={3}>
    {/* Informations générales */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MemoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Système</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Temps de fonctionnement :</Typography>
              <Typography variant="h6">{formatUptime(diagnostics.uptime || 0)}</Typography>
            </Grid>
            {diagnostics.hostname && (
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Nom :</Typography>
                <Typography variant="h6">{diagnostics.hostname}</Typography>
              </Grid>
            )}
            {diagnostics.firmware_version && (
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Version firmware :</Typography>
                <Typography variant="h6"><code style={{ fontSize: '0.8em' }}>{diagnostics.firmware_version}</code></Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Dernière mise à jour :</Typography>
              <Typography variant="body2">
                {format(new Date(diagnostics.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    {/* Carte V3 : rendu uniquement si le boitier publie ces champs */}
    {diagnostics.board && Object.values(diagnostics.board).some((v: any) => v !== null && v !== undefined) && (
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MemoryIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Carte</Typography>
            </Box>
            <Grid container spacing={2}>
              {typeof diagnostics.board.box_temp === 'number' && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Température coffret :</Typography>
                  <Typography variant="h6">{diagnostics.board.box_temp.toFixed(1)} °C</Typography>
                </Grid>
              )}
              {typeof diagnostics.board.box_humidity === 'number' && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Humidité coffret :</Typography>
                  <Typography variant="h6">{diagnostics.board.box_humidity.toFixed(1)} %</Typography>
                </Grid>
              )}
              {typeof diagnostics.board.rtc_ecart_s === 'number' ? (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Écart horloge RTC :</Typography>
                  <Typography variant="h6" color={Math.abs(diagnostics.board.rtc_ecart_s) <= 5 ? 'success.main' : 'error.main'}>
                    {diagnostics.board.rtc_ecart_s >= 0 ? '+' : ''}{diagnostics.board.rtc_ecart_s.toFixed(1)} s
                  </Typography>
                </Grid>
              ) : (diagnostics.board.rtc_ok !== null && diagnostics.board.rtc_ok !== undefined && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Horloge RTC :</Typography>
                  <Typography variant="h6" color={diagnostics.board.rtc_ok ? 'success.main' : 'error.main'}>
                    {diagnostics.board.rtc_ok ? 'synchronisée' : 'DÉFAUT'}
                  </Typography>
                </Grid>
              ))}
              {typeof diagnostics.board.watchdog_armed === 'number' && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Watchdog :</Typography>
                  <Typography variant="h6">{diagnostics.board.watchdog_armed ? `armé (${diagnostics.board.watchdog_kicks ?? 0} kicks)` : 'désactivé'}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    )}

    {/* Mémoire RAM */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Mémoire RAM</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Utilisation : {formatBytes(diagnostics.memory?.used || 0)} / {formatBytes(diagnostics.memory?.total || 0)}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={diagnostics.memory?.total > 0 ? 
                (diagnostics.memory.used / diagnostics.memory.total) * 100 : 0} 
              sx={{ mt: 1 }}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Libre : {formatBytes(diagnostics.memory?.free || 0)}
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    {/*
      Stockage (Carte SD) — LOT 7.5, fusion S-07 / CF-007.

      CE BLOC PORTAIT LE MEME DEFAUT DEPUIS AVRIL, RECOPIE TEL QUEL DANS CE
      COMPOSANT NEUF EN AOUT :
        - `diagnostics.disk.usage_percent || 0` : occupation inconnue rendue
          en 0 %, donc barre vide, donc `color="primary"` (bleu calme) ;
        - `formatBytes(... || 0)` : « 0 B / 0 B » au lieu de « indisponible » ;
        - la carte n'apparaissait que si `total > 0`, ce qui MASQUAIT
          entièrement le cas « je ne sais rien » au lieu de le nommer.

      Un disque plein dont le collecteur n'arrive plus à écrire produit cette
      signature. L'opérateur lisait alors « disque OK ».

      La carte est désormais toujours affichée. Elle indique soit l'occupation
      mesurée, soit l'absence de mesure.
    */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Stockage (Carte SD)</Typography>
          </Box>
          {typeof diagnostics.disk?.usage_percent === 'number' ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Utilisation : {formatBytesOuIndisponible(diagnostics.disk.used)} / {formatBytesOuIndisponible(diagnostics.disk.total)}
                  {' '}({diagnostics.disk.usage_percent.toFixed(1)} %)
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, diagnostics.disk.usage_percent))}
                  sx={{ mt: 1 }}
                  color={diagnostics.disk.usage_percent > 90 ? 'error' : diagnostics.disk.usage_percent > 70 ? 'warning' : 'primary'}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Libre : {formatBytesOuIndisponible(diagnostics.disk.free)}
              </Typography>
            </>
          ) : (
            <>
              {/*
                Pas de barre du tout : une barre à 0 %, même grise, se lit
                comme une mesure. Le cas « pas de mesure » est donc rendu par
                un texte.
              */}
              <Chip
                icon={<WarningIcon />}
                label="Occupation disque indisponible"
                color="warning"
                variant="outlined"
                size="small"
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Le boîtier n'a pas publié de mesure d'occupation exploitable.
                L'occupation du disque est inconnue.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Grid>

    {/* WiFi */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WifiIcon sx={{ mr: 1 }} />
            <Typography variant="h6">WiFi</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Statut :</Typography>
              <Chip 
                label={getStatusInfo(diagnostics.wifi?.status).text}
                color={getStatusInfo(diagnostics.wifi?.status).color}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Adresse IP :</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{diagnostics.wifi?.ip_address || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Signal (RSSI) :</Typography>
              <Typography variant="body1">{diagnostics.wifi?.rssi || 0} dBm</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Reconnexions :</Typography>
              <Typography variant="body1">{diagnostics.wifi?.reconnect_count || 0}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    {/* MQTT */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">MQTT</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Statut :</Typography>
              <Chip 
                label={getStatusInfo(diagnostics.mqtt?.status).text}
                color={getStatusInfo(diagnostics.mqtt?.status).color}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Succès/Échecs :</Typography>
              <Typography variant="body1">
                {diagnostics.mqtt?.success_count || 0} / {diagnostics.mqtt?.fail_count || 0}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Reconnexions : {diagnostics.mqtt?.reconnect_count || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Dernier succès : {diagnostics.mqtt?.last_success ? 
                  format(new Date(diagnostics.mqtt.last_success * 1000), 'dd/MM/yyyy HH:mm:ss', { locale: fr }) : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    {/* Coupures datées : secteur / MQTT / WiFi (journal V3) */}
    {Array.isArray(diagnostics.event_log) && (
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WifiOffIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Coupures</Typography>
            </Box>
            <Grid container spacing={3}>
              {[
                { cle: 'secteur', titre: 'Secteur 230 V' },
                { cle: 'mqtt', titre: 'MQTT' },
                { cle: 'wifi', titre: 'WiFi' },
              ].map(({ cle, titre }) => {
                // Tous les evenements du journal (60 max cote boitier),
                // du plus recent au plus ancien ; ~10 lignes visibles,
                // defilement au-dela.
                const lignes = diagnostics.event_log
                  .filter((e: any) => e.categorie === cle)
                  .slice()
                  .reverse();
                return (
                  <Grid item xs={12} md={4} key={cle}>
                    <Typography variant="subtitle1" gutterBottom>{titre}</Typography>
                    {lignes.length === 0 ? (
                      <Typography variant="body2" color="success.main">aucune coupure enregistrée</Typography>
                    ) : (
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td': { py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.85rem' } }}>
                        <tbody>
                          {lignes.map((e: any, i: number) => (
                            <tr key={i}>
                              <td>{format(new Date(e.ts), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: e.etat === 'coupure' ? '#d32f2f' : '#2e7d32' }}>
                                {e.etat === 'coupure' ? 'coupure' : 'retour'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Box>
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    )}

    {/* Capteurs */}
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SensorsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Capteurs</Typography>
          </Box>
          {diagnostics.rs485 && typeof diagnostics.rs485.transactions === 'number' && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              {(() => {
                const capteursEnEchec = Object.entries(diagnostics.sensors || {})
                  .filter(([, c]: [string, any]) => (c?.fail_count || 0) > 0);
                const pertes = capteursEnEchec
                  .reduce((somme: number, [, c]: [string, any]) => somme + (c.fail_count || 0), 0);
                // Ne nommer que les capteurs ACTUELLEMENT en erreur (status 2),
                // comme la section Erreurs : un capteur retabli ne doit plus
                // etre accuse, meme si son cumul de pertes reste affiche.
                const capteursEnErreur = capteursEnEchec
                  .filter(([, c]: [string, any]) => c?.status === 2)
                  .map(([nom, c]: [string, any]) => `${nom} (${c.fail_count})`);
                const parSonde: Record<string, any> = diagnostics.rs485.par_sonde || {};
                const adresses = Object.keys(parSonde).sort((a, b) => Number(a) - Number(b));
                const somme = (cle: string) => adresses.reduce((s, a) => s + (parSonde[a]?.[cle] || 0), 0);
                // Distribution par sonde : « adresse 4 (3) » = 3 occurrences
                const sondesEn = (cle: string) => adresses
                  .filter((a) => (parSonde[a]?.[cle] || 0) > 0)
                  .map((a) => `adresse ${a} (${parSonde[a][cle]})`);
                const totalTrames = adresses.reduce(
                  (s, a) => s + (parSonde[a]?.transactions || 0), 0);
                const erreursReelles = somme('timeouts') + somme('crc') + somme('exceptions');
                const tauxReussite = totalTrames > 0
                  ? 100 * (1 - erreursReelles / totalTrames) : null;
                const tuiles = adresses.length > 0 ? [
                  { titre: 'Pertes de données', valeur: pertes, sondes: capteursEnErreur, grave: true },
                  { titre: 'Sans réponse', valeur: somme('timeouts'), sondes: sondesEn('timeouts'), grave: false },
                  { titre: 'Trames corrompues', valeur: somme('crc'), sondes: sondesEn('crc'), grave: false },
                  { titre: 'Erreurs protocole', valeur: somme('exceptions'), sondes: sondesEn('exceptions'), grave: false },
                ] : [
                  // Firmware antérieur : seuls les totaux existent
                  { titre: 'Pertes de données', valeur: pertes, sondes: capteursEnErreur, grave: true },
                  { titre: 'Transactions', valeur: diagnostics.rs485.transactions, sondes: [], grave: false },
                  { titre: 'Échecs (tous confondus)', valeur: diagnostics.rs485.echecs, sondes: [], grave: false },
                ];
                const largeur = tuiles.length === 3 ? 4 : 3;
                return (
                  <>
                  <Typography variant="subtitle2" gutterBottom>
                    Bus RS485
                    {tauxReussite !== null && (
                      <Typography component="span" variant="caption" sx={{ ml: 1.5 }}
                        color={erreursReelles > 0 ? 'warning.main' : 'success.main'}>
                        taux de réussite : {tauxReussite.toFixed(2)} % ({totalTrames} trames)
                      </Typography>
                    )}
                  </Typography>
                  <Grid container spacing={2}>
                    {tuiles.map((t) => (
                      <Grid item xs={6} sm={largeur} key={t.titre}>
                        <Typography variant="body2" color="textSecondary">{t.titre}:</Typography>
                        <Typography variant="h6" color={t.valeur > 0 ? (t.grave ? 'error.main' : 'warning.main') : 'success.main'}>
                          {t.valeur}
                        </Typography>
                        {t.valeur > 0 && t.sondes.length > 0 && (
                          <Typography variant="caption" color={t.grave ? 'error.main' : 'warning.main'} sx={{ display: 'block' }}>
                            {t.sondes.length > 1 ? 'sondes ' : 'sonde '}{t.sondes.join(', ')}
                          </Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                  </>
                );
              })()}
            </Box>
          )}
          <Grid container spacing={3}>
            {diagnostics.sensors && Object.entries(diagnostics.sensors).map(([sensorName, sensorData]: [string, any]) => (
              <Grid item xs={12} md={6} key={sensorName}>
                <Typography variant="subtitle1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {sensorName.replace(/_/g, ' ').replace(/(\d+)/g, ' $1')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Statut :</Typography>
                    <Chip 
                      label={getStatusInfo(sensorData.status).text}
                      color={getStatusInfo(sensorData.status).color}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Taux de succès :</Typography>
                    <Typography variant="body1">{sensorData.success_rate ?? 0}%</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Lectures : {sensorData.success_count ?? 0} succès, {sensorData.fail_count ?? 0} échecs
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Dernière lecture : {sensorData.last_read ? 
                        format(new Date(sensorData.last_read * 1000), 'dd/MM/yyyy HH:mm:ss', { locale: fr }) : 'Jamais'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
            {(!diagnostics.sensors || Object.keys(diagnostics.sensors).length === 0) && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Aucun capteur configuré</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    {/* Stockage local */}
    {diagnostics.storage && (
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Stockage</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Lectures stockées :</Typography>
                <Typography variant="h6">{diagnostics.storage.stored_count || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Erreurs :</Typography>
                <Typography variant="h6">{diagnostics.storage.errors || 0}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    )}

    {/* Erreurs Récentes - filtrées par fenêtre temporelle et dédupliquées */}
    <Grid item xs={12}>
      {(() => {
        const filteredErrors = (filtrerErreurs || filtreParDefaut)(diagnostics.recent_errors, diagnostics.sensors);
        const hasErrors = filteredErrors.length > 0;
        return (
          <Card sx={{ borderLeft: hasErrors ? '4px solid #f44336' : '4px solid #4caf50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ mr: 1, color: hasErrors ? 'error.main' : 'success.main' }} />
                <Typography variant="h6">
                  Erreurs {hasErrors 
                    ? `(${filteredErrors.length} type${filteredErrors.length > 1 ? 's' : ''})` 
                    : '(Aucune sur la période)'}
                </Typography>
              </Box>
              {hasErrors ? (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {filteredErrors.map((err: any, idx: number) => (
                    <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          [{err.code}] {err.message}
                        </Typography>
                        {err.count > 1 && (
                          <Chip label={`x${err.count}`} size="small" color="error" variant="outlined" />
                        )}
                      </Box>
                      {err.details && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          {err.details}
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Dernière occurrence : {format(new Date(err.ts), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Aucune erreur sur la période sélectionnée{libellePeriode ? ` (${libellePeriode})` : ''}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })()}
    </Grid>
  </Grid>
) : (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography color="text.secondary">
      Aucune donnée de diagnostic disponible
    </Typography>
  </Box>
)}
  </>
);

export default DiagnosticsPanel;
